import React, { useRef } from 'react';
import { Modal, Form, Input, Checkbox, message, Row, Col, Divider, Typography } from 'antd';
import { useCreateContractorMutation, useUpdateContractorMutation } from '../../store/api/referencesApi';
import type { Contractor, ContractorRole, CreateContractorRequest } from '../../store/api/referencesApi';
import AddressAutocomplete from './AddressAutocomplete';
import {
  buildFormHydrationSessionKey,
  defaultRolesStableKey,
  shouldHydrateFormSession,
} from '../../utils/formHydrationSession';

export type { ContractorRole } from '../../store/api/referencesApi';

export type CreateContractorFormData = CreateContractorRequest;

const FORM_ROLES: ContractorRole[] = ['client', 'expeditor', 'shipper', 'consignee'];

/** Stable empty array — never use `defaultRoles = []` in props (new ref each render). */
export const EMPTY_CONTRACTOR_DEFAULT_ROLES: ContractorRole[] = [];

export const CONTRACTOR_DEFAULT_ROLES_CLIENT: ContractorRole[] = ['client'];
export const CONTRACTOR_DEFAULT_ROLES_SHIPPER: ContractorRole[] = ['shipper'];
export const CONTRACTOR_DEFAULT_ROLES_CONSIGNEE: ContractorRole[] = ['consignee'];
export const CONTRACTOR_DEFAULT_ROLES_EXPEDITOR: ContractorRole[] = ['expeditor'];

interface CreateContractorModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: (contractor: Contractor) => void;
  contractor?: Contractor | null;
  defaultRoles?: ContractorRole[];
  title?: string;
}

const roleLabels: Record<ContractorRole, string> = {
  client: 'Клиент',
  expeditor: 'Экспедитор',
  shipper: 'Грузоотправитель',
  consignee: 'Грузополучатель',
};

const { Text } = Typography;

const ACCOUNT_ERR = 'Введите корректный расчетный счет (20 цифр)';

function contractorToFormValues(contractor: Contractor): CreateContractorFormData {
  return {
    name: contractor.name,
    address: contractor.address,
    contactPerson: contractor.contactPerson,
    phone: contractor.phone,
    email: contractor.email ?? undefined,
    inn: contractor.inn,
    roles: contractor.roles.filter((r): r is ContractorRole =>
      FORM_ROLES.includes(r as ContractorRole)
    ),
    kpp: contractor.kpp ?? undefined,
    ogrn: contractor.ogrn ?? undefined,
    directorName: contractor.directorName ?? undefined,
    bankName: contractor.bankName ?? undefined,
    bik: contractor.bik ?? undefined,
    accountNumber: contractor.accountNumber ?? undefined,
    corrAccount: contractor.corrAccount ?? undefined,
  };
}

const CreateContractorModal: React.FC<CreateContractorModalProps> = ({
  isOpen,
  onCancel,
  onSuccess,
  contractor = null,
  defaultRoles,
  title = 'Добавить нового контрагента',
}) => {
  const resolvedDefaultRoles = defaultRoles ?? EMPTY_CONTRACTOR_DEFAULT_ROLES;
  const createRolesKey = defaultRolesStableKey(resolvedDefaultRoles);
  const [form] = Form.useForm<CreateContractorFormData>();
  const [createContractor, { isLoading: isCreating }] = useCreateContractorMutation();
  const [updateContractor, { isLoading: isUpdating }] = useUpdateContractorMutation();
  const hydratedSessionRef = useRef<string | null>(null);

  const rolesWatch = Form.useWatch('roles', form) as ContractorRole[] | undefined;
  const showLegalAndBank = Boolean(rolesWatch?.includes('client'));

  const isLoading = isCreating || isUpdating;
  const isEditMode = !!contractor;

  const handleSubmit = async (values: CreateContractorFormData) => {
    try {
      if (!values.roles || values.roles.length === 0) {
        message.error('Выберите хотя бы одну роль для контрагента');
        return;
      }

      if (isEditMode && contractor) {
        const updatedContractor = await updateContractor({
          id: contractor.id,
          data: values,
        }).unwrap();

        message.success('Контрагент успешно обновлен');
        onSuccess(updatedContractor);
      } else {
        const newContractor = await createContractor(values).unwrap();

        message.success('Контрагент успешно создан');
        onSuccess(newContractor);
      }

      hydratedSessionRef.current = null;
      form.resetFields();
      onCancel();
    } catch (error: any) {
      console.error('Error creating/updating contractor:', error);
      const errorMessage = error?.data?.error || 'Ошибка при создании/обновлении контрагента';
      message.error(errorMessage);
    }
  };

  const handleCancel = () => {
    hydratedSessionRef.current = null;
    form.resetFields();
    onCancel();
  };

  React.useEffect(() => {
    const sessionKey = buildFormHydrationSessionKey({
      isOpen,
      editEntityId: contractor?.id,
      createExtraKey: createRolesKey,
    });

    if (!shouldHydrateFormSession(hydratedSessionRef.current, sessionKey)) {
      if (!isOpen) hydratedSessionRef.current = null;
      return;
    }

    hydratedSessionRef.current = sessionKey;

    if (contractor) {
      form.setFieldsValue(contractorToFormValues(contractor));
      return;
    }

    form.resetFields();
    if (resolvedDefaultRoles.length > 0) {
      form.setFieldsValue({ roles: [...resolvedDefaultRoles] });
    }
  }, [isOpen, contractor, createRolesKey, form, resolvedDefaultRoles]);

  return (
    <Modal
      title={isEditMode ? 'Редактировать контрагента' : title}
      open={isOpen}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      okText={isEditMode ? 'Обновить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={isLoading}
      width={720}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Название контрагента"
          name="name"
          rules={[
            { required: true, message: 'Введите название контрагента' },
            { min: 2, message: 'Название должно содержать минимум 2 символа' },
          ]}
        >
          <Input placeholder="Например: ООО 'Транс-Логистик'" maxLength={255} />
        </Form.Item>

        <Form.Item
          label="Роли контрагента"
          name="roles"
          rules={[
            { required: true, message: 'Выберите хотя бы одну роль' },
            {
              validator: (_, value) => {
                if (!value || value.length === 0) {
                  return Promise.reject(new Error('Выберите хотя бы одну роль'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Checkbox.Group>
            <Row gutter={[16, 8]}>
              {FORM_ROLES.map((role) => (
                <Col span={12} key={role}>
                  <Checkbox value={role}>{roleLabels[role]}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>

        <Form.Item label="Адрес" name="address">
          <AddressAutocomplete placeholder="Юридический адрес контрагента" style={{ width: '100%' }} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Контактное лицо" name="contactPerson">
              <Input placeholder="ФИО контактного лица" maxLength={255} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Телефон" name="phone">
              <Input placeholder="+7 (xxx) xxx-xx-xx" maxLength={50} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Email"
          name="email"
          rules={[
            {
              validator: (_, v) => {
                const s = String(v ?? '').trim();
                if (!s) return Promise.resolve();
                const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
                return ok ? Promise.resolve() : Promise.reject(new Error('Некорректный email'));
              },
            },
          ]}
        >
          <Input type="email" placeholder="name@company.ru" maxLength={255} autoComplete="email" />
        </Form.Item>

        <Form.Item
          label="ИНН"
          name="inn"
          rules={[
            {
              validator: (_, v) => {
                const s = String(v ?? '').trim();
                if (!s) return Promise.resolve();
                return /^\d{10}$|^\d{12}$/.test(s)
                  ? Promise.resolve()
                  : Promise.reject(new Error('ИНН — 10 или 12 цифр'));
              },
            },
          ]}
        >
          <Input placeholder="Налоговый номер" maxLength={12} />
        </Form.Item>

        {showLegalAndBank ? (
          <>
            <Divider style={{ margin: '16px 0' }} />
            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              Реквизиты
            </Text>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="КПП" name="kpp">
                  <Input placeholder="КПП" maxLength={9} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="ОГРН" name="ogrn">
                  <Input placeholder="ОГРН / ОГРНИП" maxLength={15} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="ФИО директора" name="directorName">
                  <Input placeholder="Как в уставе" maxLength={255} />
                </Form.Item>
              </Col>
            </Row>

            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              Банк
            </Text>
            <Form.Item label="Наименование банка" name="bankName">
              <Input placeholder="Банк" maxLength={255} />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="БИК"
                  name="bik"
                  dependencies={['bankName']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const bankName = String(getFieldValue('bankName') ?? '').trim();
                        const bik = String(value ?? '').trim();
                        if (bankName !== '') {
                          if (!bik) return Promise.reject(new Error('Укажите БИК банка'));
                          if (!/^\d{9}$/.test(bik)) return Promise.reject(new Error('БИК должен содержать 9 цифр'));
                          return Promise.resolve();
                        }
                        if (bik && !/^\d{9}$/.test(bik)) {
                          return Promise.reject(new Error('БИК должен содержать 9 цифр'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input placeholder="9 цифр" maxLength={9} inputMode="numeric" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Расчётный счёт"
                  name="accountNumber"
                  dependencies={['bankName']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const bankName = String(getFieldValue('bankName') ?? '').trim();
                        const acc = String(value ?? '').trim();
                        if (bankName !== '') {
                          if (!acc) return Promise.reject(new Error('Укажите расчётный счёт'));
                          if (!/^\d{20}$/.test(acc)) return Promise.reject(new Error(ACCOUNT_ERR));
                          return Promise.resolve();
                        }
                        if (acc && !/^\d{20}$/.test(acc)) {
                          return Promise.reject(new Error(ACCOUNT_ERR));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input placeholder="20 цифр" maxLength={20} inputMode="numeric" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Корсчёт"
                  name="corrAccount"
                  rules={[
                    {
                      validator(_, value) {
                        const s = String(value ?? '').trim();
                        if (!s) return Promise.resolve();
                        return /^\d{20}$/.test(s)
                          ? Promise.resolve()
                          : Promise.reject(new Error('Корсчёт должен содержать 20 цифр'));
                      },
                    },
                  ]}
                >
                  <Input placeholder="20 цифр" maxLength={20} inputMode="numeric" />
                </Form.Item>
              </Col>
            </Row>
          </>
        ) : null}
      </Form>
    </Modal>
  );
};

export default CreateContractorModal;
